'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { axiosClient } from '@nx-frontend/shared/api';
import { $aiHelper } from '@nx-frontend/shared/store/ai-helper';
import { $editorAttachmentFullscreen } from '@nx-frontend/shared/store/editor-attachment';
import { $menuState, setActiveItemIdEvent } from '@nx-frontend/shared/store/feature-menu-tree';
import { $isFreePlan } from '@nx-frontend/shared/store/org-info';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { clearBreadcrumbsSpecialPagesVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { AiMentionType, MenuType, OrgMemberRole, UnleashFeatures, User } from '@nx-frontend/shared/types';
import { closeAiModalEvent, openAiModalEvent } from './ai-modal';
import { ChatbotFeedbackFormPopup, ChatbotFeedbackSuccessPopup } from './ai-popups';
import { ChatbotAddToPagePopup } from './ai-popups/chatbot-add-page-popup';
import { trackAddAIResponseToPage, trackSubmitMessageFeedback, trackThreadDelete } from './analytics';
import { ChatbotContext, OnPageNavigateOptions } from './chatbot-context';
import { ChatbotUi } from './chatbot-ui';
import {
  $aiPortalVisitorDetails,
  aiAddMessageToPageEvent,
  deleteAiThreadEvent,
  initPortalVisitorDetailsEvent,
  markMessageDislikeEvent,
  setActiveThreadIdEvent,
  updateAiMainPageEvent,
  updateAiThreadMessagesEvent,
  updateAiWorkspaceThreadsEvent,
  updateWorkspaceIndexingEvent,
} from './store';
import './styles.scss';
import { ChatbotMessage } from './types';
import { getCurrentLocation } from './util';

export interface AiHelperProps {
  hideAiHelper: () => void;
  showUpgradePopup?: () => void;
  apiHost: string;
  // create message uses different endpoint, but this prop is optional and only for dev purposes
  apiHostCreateMessage?: string;
  workspaceId: string;
  /**
   * activates portal mode
   */
  portalId?: string;
  sessionId?: string | null;
  portalSettings?: {
    id: string;
    name: string;
    domain: string;
    hasCustomizerAccess: boolean;
  };
  workspaceSettings?: {
    title: string;
    logo?: {
      storedFileUUID: string;
    };
    color: string;
  };
  user: User;
  role: { role: OrgMemberRole | 'visitor' };
  /**
   * Whether current ENV is development
   */
  isDev: boolean;
  routerPush: (url: string) => void;
  allowCreatePage?: boolean;
  showInfoMenu?: boolean;
  feedbackBottomText?: string;
  orgId?: string;
}

// fetches eversessionid cookie from document.cookie

export function AiHelper(props: AiHelperProps) {
  const [
    setActiveThreadId,
    updateAiMainPage,
    updateAiWorkspaceThreads,
    deleteAiThread,
    updateAiThreadMessages,
    aiAddMessageToPage,
    openAiModal,
    closeAiModal,
    setActiveItemId,
    updateWorkspaceIndexing,
    markMessageDislike,
    isFreePlan,
    menuState,
    editorAttachmentFullscreen,
    initPortalVisitorDetails,
    aiPortalVisitorDetails,
    unleashFlags,
    clearBreadcrumbsSpecialPagesVisited,
    aiHelper,
  ] = useUnit([
    setActiveThreadIdEvent,
    updateAiMainPageEvent,
    updateAiWorkspaceThreadsEvent,
    deleteAiThreadEvent,
    updateAiThreadMessagesEvent,
    aiAddMessageToPageEvent,
    openAiModalEvent,
    closeAiModalEvent,
    setActiveItemIdEvent,
    updateWorkspaceIndexingEvent,
    markMessageDislikeEvent,
    $isFreePlan,
    $menuState,
    $editorAttachmentFullscreen,
    initPortalVisitorDetailsEvent,
    $aiPortalVisitorDetails,
    $unleashFlags,
    clearBreadcrumbsSpecialPagesVisitedEvent,
    $aiHelper,
  ]);
  const {
    hideAiHelper,
    apiHost,
    apiHostCreateMessage,
    workspaceId,
    user,
    showUpgradePopup,
    sessionId,
    portalSettings,
    workspaceSettings,
    role,
    isDev,
    routerPush,
    portalId,
    allowCreatePage = true,
    showInfoMenu = true,
    feedbackBottomText,
    orgId,
  } = props;

  const [useWeb, setUseWeb] = useState(false);

  const { toast } = useToast();

  const t = useTranslations('features');

  const currentLocation = getCurrentLocation(
    editorAttachmentFullscreen,
    menuState,
    !unleashFlags[UnleashFeatures.AI_ASSISTANT_DETECT_USER_MESSAGE_CONTEXT]
  );

  useEffect(() => {
    if (portalId) {
      initPortalVisitorDetails();
    }
  }, [initPortalVisitorDetails, portalId]);

  useEffect(() => {
    // everytime workspace/main page/ changes, reset the chat state
    // do not reset for portals
    if (!portalId) {
      setActiveThreadId(null);
    }
  }, [workspaceId, setActiveThreadId, portalId]);
  // on mount chat get prompts and last threads
  useEffect(() => {
    if (portalId && !aiPortalVisitorDetails?.portalVisitorId) {
      // wait until store initializes
      return;
    }
    updateAiMainPage({
      workspaceId,
      apiHost,
      sessionId,
      userId: user.id,
      portalId,
      portalVisitorId: role.role === 'visitor' ? aiPortalVisitorDetails?.portalVisitorId : undefined,
    });
  }, [
    apiHost,
    sessionId,
    updateAiMainPage,
    updateWorkspaceIndexing,
    user.id,
    workspaceId,
    portalId,
    aiPortalVisitorDetails,
    role,
  ]);

  const onPageNavigate = useCallback(
    (type: AiMentionType | 'workspace' | 'portal' | 'tasks', globalId: string, options: OnPageNavigateOptions = {}) => {
      if (options.url) {
        document.location.href = options.url;
        return;
      }
      if (options.routeUrl) {
        routerPush(options.routeUrl);
        return;
      }

      switch (type) {
        case AiMentionType.NOTE:
          clearBreadcrumbsSpecialPagesVisited();
          routerPush(`/space/${workspaceId}/page/${globalId}`);
          setActiveItemId({ menuType: MenuType.WEBNOTES_MENU, id: `note#${globalId}` });
          break;
        case AiMentionType.FOLDER:
          clearBreadcrumbsSpecialPagesVisited();
          routerPush(`/space/${workspaceId}/folder/${globalId}`);
          setActiveItemId({ menuType: MenuType.WEBNOTES_MENU, id: `notesFolder#${globalId}` });
          break;
        case 'workspace':
          routerPush(`/space/${workspaceId}/all`);
          break;
        case 'tasks':
          if (portalId) {
            // also close the active task if opened
            if (document.location.pathname.startsWith('/tasks-dashboard')) {
              document.dispatchEvent(new CustomEvent('ng2:tasks:close-task', {}));
              document.dispatchEvent(
                new CustomEvent('ng2:tasks:open-task', {
                  detail: {
                    taskId: globalId,
                    forceSetDetailedTask: true,
                    waitForScroll: true,
                  },
                })
              );
            } else {
              routerPush(`/tasks-dashboard?task-id=${encodeURIComponent(globalId)}&visit=${new Date().getTime()}`);
            }
          } else {
            routerPush(`/space/${workspaceId}/tasks/${globalId}`);
          }
          break;
        case 'portal':
        case AiMentionType.PORTAL_NOTE:
        case AiMentionType.PORTAL_SECTION:
          portalSettings?.domain && window.open(`https://${portalSettings?.domain}`, '_blank');
          break;
      }
    },
    [portalSettings?.domain, routerPush, setActiveItemId, workspaceId, portalId, clearBreadcrumbsSpecialPagesVisited]
  );

  const onUpdateThreads = useCallback(() => {
    updateAiWorkspaceThreads({
      workspaceId,
      portalId,
      apiHost,
      sessionId,
      portalVisitorId: role.role === 'visitor' ? aiPortalVisitorDetails?.portalVisitorId : undefined,
    });
  }, [apiHost, sessionId, updateAiWorkspaceThreads, workspaceId, portalId, role, aiPortalVisitorDetails]);

  const onUpdateThreadMessages = useCallback(
    (threadId: string) => {
      updateAiThreadMessages({
        workspaceId,
        apiHost,
        sessionId,
        threadId,
        portalId,
        portalVisitorId: role.role === 'visitor' ? aiPortalVisitorDetails?.portalVisitorId : undefined,
      });
    },
    [apiHost, sessionId, updateAiThreadMessages, workspaceId, portalId, aiPortalVisitorDetails, role]
  );

  const onThreadDelete = useCallback(
    (id: string) => {
      openAiModal({
        title: t('ai-helper.chat_removal'),
        content: t('ai-helper.delete_chat_confirmation'),
        buttons: [
          {
            text: t('ai-helper.no'),
            size: 'md',
            classes: ['!px-6'],
            onClick: () => {
              closeAiModal();
            },
          },
          {
            text: t('ai-helper.yes'),
            main: true,
            size: 'md',
            classes: ['!px-6'],
            onClick: () => {
              trackThreadDelete();
              deleteAiThread({
                workspaceId,
                apiHost,
                sessionId,
                threadId: id,
                portalVisitorId: role.role === 'visitor' ? aiPortalVisitorDetails?.portalVisitorId : undefined,
              });
              closeAiModal();
            },
          },
        ],
      });
    },
    [apiHost, closeAiModal, deleteAiThread, openAiModal, sessionId, workspaceId, role, aiPortalVisitorDetails, t]
  );

  const onAddMessageToPage = useCallback(
    (text: string, threadId: string) => {
      const localStorageKey = 'fusebase-allow-ai-add-to-page';
      const isAllowAdd = localStorage.getItem(localStorageKey) === 'true';
      if (!isAllowAdd) {
        openAiModal({
          title: t('ai-helper.add_to_new_page'),
          content: <ChatbotAddToPagePopup localStorageKey={localStorageKey} />,
          buttons: [
            {
              text: t('ai-helper.cancel'),
              size: 'md',
              classes: ['!px-6'],
              onClick: () => {
                closeAiModal();
              },
            },
            {
              text: t('ai-helper.add_to_page'),
              main: true,
              size: 'md',
              classes: ['!px-6'],
              onClick: () => {
                aiAddMessageToPage({ workspaceId, apiHost, sessionId, threadId, text });
                closeAiModal();
                trackAddAIResponseToPage();
              },
            },
          ],
        });
      } else {
        aiAddMessageToPage({ workspaceId, apiHost, sessionId, threadId, text });
        trackAddAIResponseToPage();
      }
    },
    [aiAddMessageToPage, apiHost, closeAiModal, openAiModal, sessionId, workspaceId, t]
  );

  const onFeedback = useCallback(
    (threadId: string, message: ChatbotMessage /* TODO for send message id */) => {
      const onFeedbackSubmit = async (text: string) => {
        closeAiModal();
        trackSubmitMessageFeedback();
        try {
          const response = await axiosClient.post(
            `/ai-assistant/rest/workspaces/${workspaceId}/threads/${threadId}/feedback`,
            { text },
            {
              headers: {
                'Content-Type': 'application/json',
                'everhelper-session-id': sessionId,
              },
            }
          );
          if (response.data.errorCode === 0) {
            if (message.id) {
              markMessageDislike({ threadId, messageId: message.id });
            }
            openAiModal({
              title: t('ai-helper.thanks_for_your_feedback'),
              content: <ChatbotFeedbackSuccessPopup />,
            });
          } else {
            throw new Error('Unknown error');
          }
        } catch (err) {
          toast({ title: t('ai-helper.sorry_request_not_sent'), description: t('network_error_occured') });
        }
      };

      openAiModal({
        title: t('ai-helper.help_us_understand_whats_wrong'),
        content: (
          <ChatbotFeedbackFormPopup
            themeColors={webnotesThemeColors}
            themeStyle={webnotesThemeStyle}
            onSubmit={onFeedbackSubmit}
            feedbackBottomText={feedbackBottomText}
          />
        ),
      });
    },
    [closeAiModal, markMessageDislike, openAiModal, sessionId, toast, workspaceId, feedbackBottomText, t]
  );

  const onUpdateWorkspaceIndexing = useCallback(
    (searchEnabled: boolean) => {
      updateWorkspaceIndexing({ workspaceId, apiHost, sessionId, searchEnabled });
    },
    [apiHost, sessionId, updateWorkspaceIndexing, workspaceId]
  );

  const showLimitToast = useCallback(() => {
    if (role.role === OrgMemberRole.OWNER) {
      toast({
        title: (
          <span>
            {t.rich('ai-helper.ai_addon_is_in_beta', {
              link: (chunks) => (
                <a
                  className="underline"
                  rel="noreferrer"
                  target="_blank"
                  href="https://share.hsforms.com/1GU-hPq0aRq-Xkz9ftUXt2wcsi4h"
                >
                  {chunks}
                </a>
              ),
            })}
          </span>
        ),
      });
    } else {
      toast({
        title: <span>{t('ai-helper.you_have_reached_the_limit')}</span>,
      });
    }
  }, [role, toast, t]);

  const onShowUpgradePopup = useCallback(() => {
    if (showUpgradePopup) {
      showUpgradePopup();
    } else {
      showLimitToast();
    }
  }, [showUpgradePopup, showLimitToast]);

  const onShowAddonPopup = useCallback(() => {
    showLimitToast();
  }, [showLimitToast]);

  const onSwitchUseWeb = useCallback((value: boolean) => {
    setUseWeb(value);
  }, []);

  if (!aiHelper.visible) {
    return null;
  }

  return (
    <ChatbotContext.Provider
      value={{
        apiHost,
        sessionId,
        workspaceId,
        workspaceSettings,
        portalSettings,
        user,
        onShowAddonPopup,
        onShowUpgradePopup,
        onPageNavigate,
        isOrgOwner: role.role === OrgMemberRole.OWNER,
        isVisitor: role.role === 'visitor',
        isFreePlan,
        portalId,
        role,
        apiHostCreateMessage,
        agentId: aiHelper.agentId,
        openExistingAgentThread: aiHelper.openExistingAgentThread,
        orgId,
        themeColors: webnotesThemeColors,
        themeStyle: webnotesThemeStyle,
      }}
    >
      <ChatbotUi
        isDev={isDev}
        themeColors={webnotesThemeColors}
        themeStyle={webnotesThemeStyle}
        onClose={hideAiHelper}
        updateThreads={onUpdateThreads}
        updateThreadMessages={onUpdateThreadMessages}
        onThreadDelete={onThreadDelete}
        onAddMessageToPage={onAddMessageToPage}
        onFeedback={onFeedback}
        onUpdateWorkspaceIndexing={onUpdateWorkspaceIndexing}
        onShowUpgradePopup={onShowUpgradePopup}
        onShowAddonPopup={onShowAddonPopup}
        useWeb={useWeb}
        onSwitchUseWeb={onSwitchUseWeb}
        currentLocation={currentLocation}
        allowCreatePage={allowCreatePage}
        showInfoMenu={showInfoMenu}
      />
    </ChatbotContext.Provider>
  );
}

export default AiHelper;
